import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { NbIconLibraries, NbMenuItem } from '@nebular/theme';
import { BannerOptions, BlogListOptions, GridOptions, NewsLetterOptions, RendererType, RichTextOptions, RowOptions, SectionData, SliderOptions, TestimonialOptions, VisibilityEnum } from '@urbannex/storethemes';
import { SectionEditorStoreService } from '../../dialogs/section-editor-dialog/section-editor-store.service';
import { EditorInputField } from '../../../editor-container/editor-model';
import { CssServicesService } from '../../../services/css-services.service';
import { sectionEditorIcons } from '../../dialogs/section-editor-dialog/section-dialog-menu-items';


@Injectable({
  providedIn: 'root'
})
/**
 * Service to manage section options and field definitions
 * Handles dynamic field generation based on renderer type
 * Manages synchronization with SectionEditorStoreService
 * Manages color options and other dynamic properties
 * Provides observable for menu items to be used in sidebar
 * Options:
 * - Banner, RichText, Row, Grid, BlogList, Slider, Testimonials, NewsLetter
 * 
 */
export class SectionOptionsService {
  private currentSectionData: SectionData | null = null;
  private rendererType: RendererType;
  private sectionOptions: BannerOptions | RichTextOptions | RowOptions | GridOptions | BlogListOptions | SliderOptions | TestimonialOptions | NewsLetterOptions;
  private sectionOptionsType: string;
  private editorInputFields: EditorInputField[] = [];
  
  // Color management
  private bgColors$: Subscription;
  private pmColors$: Subscription;
  private bgBasic: string[] = [];
  private bgPrimary: string[] = [];
  private bgAlt: string[] = [];
  private fillColors: string[];
  private primaryColors: string[] = [];
  defaultIcon = {icon:"text-edit",pack:"section-menu-icons"}

  // Subject for menu items
  private menuItemsSubject = new BehaviorSubject<NbMenuItem[]>([]);
  public menuItems$ = this.menuItemsSubject.asObservable();

  constructor(
    private sectionEditorStore: SectionEditorStoreService,
    private cssService: CssServicesService,
    private iconLibraries: NbIconLibraries
  ) {}

  /**
   * Initialize the service with section data
   * 1. Registers the icons to use
   * 2.
   */
  initializeSectionOptions(sectionData: SectionData): void {
    this.iconLibraries.registerSvgPack('section-menu-icons',sectionEditorIcons);
    console.log('Initializing Section Options with data:', sectionData);
    this.currentSectionData = sectionData;
    /**
     * Set renderer type and options based on sectionData
     */
    this.setRendererTypeAndOptions(sectionData);
    /**
     * Set editor input fields based on renderer type
     */
    this.setEditorInputFields();
    /**
     * Populate field values from current section data
     */
    this.populateFieldValues();
    /**
     * Generate menu items for sidebar
     */
    this.generateMenuItems();
  }

  /**
   * Get field definitions for a specific renderer type
   */
  getFieldDefinitions(rendererType: RendererType): EditorInputField[] {
    switch (rendererType) {
      case RendererType.GRID:
        return this.getGridFieldDefinitions();
      case RendererType.ROW:
        return this.getRowFieldDefinitions();
      case RendererType.RICH_TEXT:
        return this.getRichTextFieldDefinitions();
      case RendererType.BANNER:
        return this.getBannerFieldDefinitions();
      case RendererType.BLOGLIST:
        return this.getBlogListFieldDefinitions();
      case RendererType.SLIDER:
        return this.getSliderFieldDefinitions();
      case RendererType.TESTIMONIALS:
        return this.getTestimonialFieldDefinitions();
      case RendererType.NEWSLETTER:
        return this.getNewsletterFieldDefinitions();
      default:
        return [];
    }
  }

  /**
   * Update field value and sync with store
   */
  updateFieldValue(fieldName: string, value: any): void {
    const field = this.editorInputFields.find(f => f.name === fieldName);
    if (field) {
      field.value = value;
    }

    if (this.currentSectionData) {
      const updatedSectionData = { ...this.currentSectionData };
      
      if (this.isMainSectionDataField(fieldName)) {
        (updatedSectionData as any)[fieldName] = value;
      } else {
        updatedSectionData[this.sectionOptionsType] = {
          ...updatedSectionData[this.sectionOptionsType],
          [fieldName]: value
        };
      }
      
      this.currentSectionData = updatedSectionData;
      this.sectionEditorStore.updateSectionData(updatedSectionData);
    }
  }

  /**
   * Get current section data
   */
  getCurrentSectionData(): SectionData | null {
    return this.currentSectionData;
  }

  /**
   * Get current field definitions
   */
  getCurrentFieldDefinitions(): EditorInputField[] {
    return this.editorInputFields;
  }
  /**
   * 
   * @param sectionData SectionData to extract renderer type and options from
   * Sets rendererType, sectionOptions, and sectionOptionsType based on sectionData
   * Handles various string and enum representations of rendererType
   */
  private setRendererTypeAndOptions(sectionData: SectionData): void {
    this.rendererType = sectionData.rendererType as RendererType;
    
    switch (sectionData.rendererType as RendererType | string) {
      case RendererType.BANNER:
      case "RendererType.BANNER":
      case "banner":
        this.sectionOptions = sectionData.bannerOptions;
        this.sectionOptionsType = "bannerOptions";
        break;
      case RendererType.BLOGLIST:
      case "RendererType.BLOGLIST":
      case "bloglist":
        this.sectionOptions = sectionData.blogListOptions;
        this.sectionOptionsType = "blogListOptions";
        break;
      case RendererType.GRID:
        this.sectionOptions = sectionData.gridOptions;
        this.sectionOptionsType = "gridOptions";
        break;
      case RendererType.HEROIMAGE:
      case "RendererType.HEROIMAGE":
      case "hero-image":
        this.sectionOptions = sectionData.bannerOptions;
        this.sectionOptionsType = "bannerOptions";
        break;
      case RendererType.NEWSLETTER:
      case "RendererType.NEWSLETTER":
      case "news-letter-sign-up":
        this.sectionOptions = sectionData.rowOptions;
        this.sectionOptionsType = "rowOptions";
        break;
      case RendererType.RICH_TEXT:
      case "RendererType.RICH_TEXT":
      case "rich-text":
        this.sectionOptions = sectionData.richTextOptions;
        this.sectionOptionsType = "richTextOptions";
        break;
      case RendererType.ROW:
      case "RendererType.ROW":
      case "row":
      case RendererType.SLIDER:
      case "RendererType.SLIDER":
      case "slider":
      case RendererType.CART:
      case "RendererType.CART":
      case "cart":
      case RendererType.CATEGORY:
      case "RendererType.CATEGORY":
      case "category":
      case RendererType.PRODUCT:
      case "RendererType.PRODUCT":
      case "product":
      case RendererType.STOREFEATURES:
      case "RendererType.STOREFEATURES":
      case "store-features":
        this.sectionOptions = sectionData.rowOptions;
        this.sectionOptionsType = "rowOptions";
        break;
      case RendererType.TESTIMONIALS:
      case "RendererType.TESTIMONIALS":
      case "testimonials":
        this.sectionOptions = sectionData.testimonialOptions;
        this.sectionOptionsType = "testimonialOptions";
        break;
      case RendererType.BLOG:
      case "RendererType.BLOG":
      case "blog":
        this.sectionOptions = sectionData.blogListOptions;
        this.sectionOptionsType = "blogListOptions";
        break;
      default:
        console.log(`No match found for ${sectionData.rendererType}`);
        break;
    }
  }

  private setEditorInputFields(): void {
    this.editorInputFields = this.getFieldDefinitions(this.rendererType);
  }

  private populateFieldValues(): void {
    if (!this.currentSectionData) return;

    this.editorInputFields.forEach(field => {
      if (this.currentSectionData) {
        // Check if field exists in main sectionData
        if (this.currentSectionData.hasOwnProperty(field.name)) {
        //Set values for nested fields.  This is necessary for contentElement field
          if(field.type && field.type == "contentElement"){
            //Object.assign(field.value,(this.currentSectionData as any)[field.name])
            field.value = (this.currentSectionData as any)[field.name];
          }else{
            field.value = (this.currentSectionData as any)[field.name];
          }
        } else if (this.sectionOptions && this.sectionOptions.hasOwnProperty(field.name)) {
          field.value = (this.sectionOptions as any)[field.name];
        }
      }
      if(field.type && field.type == "contentElement"){
        console.log({contentElementField:field.value});
      }
      //Handle fields with fromField property
      if(field.fromField) {
        const fromField = this.getFromField(field);
        if (fromField) {
          field.value = fromField.value;
          if (fromField.context) {
            field.context = { ...field.context, ...fromField.context };
          }
        }
      }
      // Handle special field cases
      this.handleSpecialFields(field);
    });
  }

  private handleSpecialFields(field: EditorInputField): void {
    if (field.name === 'bgColor' && field.value === '') {
      this.initializeColorFields(field);
    }
    
    if (field.name === 'title') {
      this.initializeTitleField(field);
    }
    
    if (field.name === 'rendererType') {
      field.value = this.rendererType;
    }
  }

  private initializeColorFields(field: EditorInputField): void {
    this.bgColors$ = this.cssService.getBgColors().pipe(take(1)).subscribe((colorKV: any[]) => {
      colorKV.forEach((colorObj, i) => {
        if (i < 4) {
          this.bgBasic.push(colorObj.val);
        } else if (i > 3 && i < 8) {
          this.bgPrimary.push(colorObj.val);
        } else if (i > 7) {
          this.bgAlt.push(colorObj.val);
        }
      });
    });

    this.pmColors$ = this.cssService.getPmColors().pipe(take(1))
      .subscribe((colorKV: any[]) => {
        colorKV.forEach((colorObj) => {
          this.primaryColors.push(colorObj.val);
        });
        if (this.primaryColors.length > 0) {
          field.value = this.primaryColors[1];
        }
        this.fillColors = this.bgBasic.concat(this.primaryColors).concat(this.bgAlt);
      });
  }

  private initializeTitleField(field: EditorInputField): void {
    const title = this.currentSectionData?.title;
    if (field.context && title) {
      Object.assign(field.context, title);
    }
    if (field.hasOwnProperty('text') && field['text'] !== '' && field['text'] !== undefined) {
      field.value = field['text'];
    } else {
      field.value = "Section Title";
    }
  }
  /**
   * Generates menu items from editor input fields
   * The initializeSectionOptions method must be called first to set up fields
   * Editor input fields are set based on renderer type and current section data
   * Updates the menuItemsSubject with new items
   */
  private generateMenuItems(): void {
    
    const menuItems: NbMenuItem[] = this.editorInputFields.map(field => ({
      title: field.title,
      icon: field.fieldIcon ? field.fieldIcon : this.defaultIcon,
      data: field
    }));
    console.log('Generated menu items:', menuItems);

    this.menuItemsSubject.next(menuItems);
  }

  getFromField(field: EditorInputField): EditorInputField {
    const fromField = this.editorInputFields.find(f => f.name === field.fromField);
    console.log(fromField)
    if (fromField) {
      return {
        ...field,
        context:fromField.context
      };
    }

    return field;
  }

  private isMainSectionDataField(fieldName: string): boolean {
    const mainFields = [
      'title', 'heading', 'imageOrientation', 'width', 'height', 'marginBottom',
      'layoutClass', 'cellOrientation', 'numberOfElements', 'isPreview',
      'product', 'wrapperClass', 'bgPosition', 'cellHeight', 'imgSrc'
    ];
    return mainFields.includes(fieldName);
  }

  // Field definition methods
  private getGridFieldDefinitions(): EditorInputField[] {
    return [
      {
        name: 'title',
        title: 'Title',
        type: 'contentElement',
        controlType: 'text',
        value: 'Section Title',
        popoverData:{title:'Edit Text',popoverType:'textPopover',target:'',service:'sectionEditorStoreService'},
        fieldIcon:{icon:"text-edit",pack:"section-menu-icons"},
        context: {
          visibility: VisibilityEnum,
          classes: [],
          text: '',
          isHeader: true,
        }
      },
      {
        name: 'visibility',
        title: 'Visibility',
        type: 'contentElement',
        controlType: 'radio',
        fieldIcon:{icon:"visibility",pack:"section-menu-icons"},
        popoverData:{title:'Set Visibility',popoverType:'visibilityPopover',target:'',service:'sectionEditorStoreService'},
        value: VisibilityEnum.VISIBLE,
        fromField:"title",
      },
      {
        name: 'bgColor',
        title: 'Background Color',
        type: '',
        controlType: 'color-picker',
        popoverData:{'title':'Background Color',popoverType:'background',target:'section',service:'sectionStore',property:'background'},
        value: "",
        fieldIcon:{icon:"background-color",pack:"section-menu-icons"},
        context: {
          popOver: true,
          target: "section",
          property: "background"
        }
      },
      {
        name: 'layoutClass',
        title: 'Layout Class',
        type: 'string',
        controlType: 'select',
        value: '',
        fieldIcon:{icon:"layout",pack:"section-menu-icons"},
        selectOptions: [
          { description: "3 Cells", key: "grid-3" },
          { description: "4 Cells", key: "grid-4" },
          { description: "5 Cells", key: "grid-5" },
          { description: "6 Cells", key: "grid-6" },
          { description: "7 Cells", key: "grid-7" },
          { description: "8 Cells", key: "grid-8" },
          { description: "9 Cells", key: "grid-9" }
        ]
      },
      {
        name: 'border',
        title: 'Border',
        type: 'string',
        controlType: 'select',
        value: 'vertical',
        fieldIcon:{icon:"border",pack:"section-menu-icons"},
        popoverData:{title:'Section Border',popoverType:'border',target:'section',service:'sectionEditorStoreService'},
        selectOptions: [
          { description: "Vertical", key: "vertical" },
          { description: "Horizontal", key: "horizontal" }
        ]
      },
      {
        name:'margin',
        title:'Margin',
        type:'string',
        value:'',
        controlType:'option',
        fieldIcon:{icon:"margin",pack:"section-menu-icons"},
      },
      {
        name:'padding',
        title: 'Padding',
        type:'string',
        value:'',
        controlType:'option',
        fieldIcon:{icon:"padding",pack:"section-menu-icons"},
      },
      {
        name: 'wrapperClass',
        title: 'Opacity',
        type: 'string',
        controlType: 'select',
        value: "",
        fieldIcon:{icon:"opacity",pack:"section-menu-icons"},
        selectOptions: [
          { description: "Solid", key: "solid" },
          { description: "Transparent", key: "transparent" }
        ]
      }
    ];
  }

  private getRowFieldDefinitions(): EditorInputField[] {
    // Add row-specific field definitions
    return [
      {
        name: 'title',
        title: 'Title',
        type: 'contentElement',
        controlType: 'text',
        value: 'Section Title',
        context: {
          visibility: VisibilityEnum,
          classes: [],
          text: '',
          isHeader: true,
        }
      },
      {
        name: 'visibility',
        title: 'Visibility',
        type: 'select',
        controlType: 'radio',
        value: VisibilityEnum.VISIBLE,
        fromField:"title"
      },
      {
        name: 'bgColor',
        title: 'Background Color',
        type: '',
        controlType: 'color-picker',
        value: "",
        context: {
          popOver: true,
          target: "section",
          property: "background"
        }
      }
      // Add more row-specific fields as needed
    ];
  }

  private getRichTextFieldDefinitions(): EditorInputField[] {
    // Add rich text-specific field definitions
    return [
      {
        name: 'title',
        title: 'Title',
        type: 'contentElement',
        controlType: 'text',
        value: 'Section Title',
        context: {
          visibility: VisibilityEnum,
          classes: [],
          text: '',
          isHeader: true,
        }
      },
      {
        name: 'visibility',
        title: 'Visibility',
        type: 'select',
        controlType: 'radio',
        value: VisibilityEnum.VISIBLE,
        context: {
          isHeader: true,
        }
      },
      {
        name: 'content',
        title: 'Content',
        type: 'string',
        controlType: 'textarea',
        value: '',
      }
      // Add more rich text-specific fields as needed
    ];
  }

  private getBannerFieldDefinitions(): EditorInputField[] {
    // Add banner-specific field definitions
    return [
      {
        name: 'title',
        title: 'Title',
        type: 'contentElement',
        controlType: 'text',
        value: 'Section Title',
        context: {
          visibility: VisibilityEnum,
          classes: [],
          text: '',
          isHeader: true,
        }
      },
      {
        name: 'visibility',
        title: 'Visibility',
        type: 'select',
        controlType: 'radio',
        value: VisibilityEnum.VISIBLE,
      },
      {
        name: 'bgColor',
        title: 'Background Color',
        type: '',
        controlType: 'color-picker',
        value: "",
        context: {
          popOver: true,
          target: "section",
          property: "background"
        }
      }
      // Add more banner-specific fields as needed
    ];
  }

  private getBlogListFieldDefinitions(): EditorInputField[] {
    // Add blog list-specific field definitions
    return [
      {
        name: 'title',
        title: 'Title',
        type: 'contentElement',
        controlType: 'text',
        value: 'Section Title',
        context: {
          visibility: VisibilityEnum,
          classes: [],
          text: '',
          isHeader: true,
        }
      },
      {
        name: 'visibility',
        title: 'Visibility',
        type: 'select',
        controlType: 'radio',
        value: VisibilityEnum.VISIBLE,
      },
      {
        name: 'numberOfPosts',
        title: 'Number of Posts',
        type: 'number',
        controlType: 'number',
        value: 5,
      }
      // Add more blog list-specific fields as needed
    ];
  }

  private getSliderFieldDefinitions(): EditorInputField[] {
    // Add slider-specific field definitions
    return [
      {
        name: 'title',
        title: 'Title',
        type: 'contentElement',
        controlType: 'text',
        value: 'Section Title',
        context: {
          visibility: VisibilityEnum,
          classes: [],
          text: '',
          isHeader: true,
        }
      },
      {
        name: 'visibility',
        title: 'Visibility',
        type: 'select',
        controlType: 'radio',
        value: VisibilityEnum.VISIBLE,
      },
      {
        name: 'autoplay',
        title: 'Autoplay',
        type: 'boolean',
        controlType: 'checkbox',
        value: false,
      }
      // Add more slider-specific fields as needed
    ];
  }

  private getTestimonialFieldDefinitions(): EditorInputField[] {
    // Add testimonial-specific field definitions
    return [
      {
        name: 'title',
        title: 'Title',
        type: 'contentElement',
        controlType: 'text',
        value: 'Section Title',
        context: {
          visibility: VisibilityEnum,
          classes: [],
          text: '',
          isHeader: true,
          service:'section'
        }
      },
      {
        name: 'visibility',
        title: 'Visibility',
        type: 'select',
        controlType: 'radio',
        value: VisibilityEnum.VISIBLE,
      },
      {
        name: 'showRatings',
        title: 'Show Ratings',
        type: 'boolean',
        controlType: 'checkbox',
        value: true,
      }
      // Add more testimonial-specific fields as needed
    ];
  }

  private getNewsletterFieldDefinitions(): EditorInputField[] {
    // Add newsletter-specific field definitions
    return [
      {
        name: 'title',
        title: 'Title',
        type: 'contentElement',
        controlType: 'text',
        value: 'Section Title',
        context: {
          visibility: VisibilityEnum,
          classes: [],
          text: '',
          isHeader: true,
        }
      },
      {
        name: 'visibility',
        title: 'Visibility',
        type: 'select',
        controlType: 'radio',
        value: VisibilityEnum.VISIBLE,
      },
      {
        name: 'placeholder',
        title: 'Email Placeholder',
        type: 'string',
        controlType: 'text',
        value: 'Enter your email address',
      }
      // Add more newsletter-specific fields as needed
    ];
  }

  /**
   * Cleanup subscriptions
   */
  ngOnDestroy(): void {
    if (this.bgColors$) {
      this.bgColors$.unsubscribe();
    }
    if (this.pmColors$) {
      this.pmColors$.unsubscribe();
    }
  }
}